import { refDebounced } from "@vueuse/core";
import { useFetch } from "~vue/api";
import type { Connection } from "~vue/types/connection";
import { computed, ref, watch } from "vue";

export const useConnectionsSearch = () => {
    const searchQuery = ref("");
    const debouncedQuery = refDebounced(searchQuery, 300);
    // Both callers pick people to review, so the list leaves out the user's manager and contingent workers.
    const fetchUrl = computed(() => {
        const params = new URLSearchParams({ for_review: "true" });
        if (debouncedQuery.value) {
            params.set("search", debouncedQuery.value);
        }
        return `/api/connections/?${params.toString()}`;
    });

    // Search is rate-limited to one request per second per user; a residual 429
    // (e.g. the same user in another tab) is not a list-destroying error, so the
    // error body never replaces `data` — the last good result stays put.
    const {
        data,
        isFetching: isFetchingConnections,
        error: connectionsFetchError,
        execute,
    } = useFetch(fetchUrl, { refetch: true, updateDataOnError: false }).get().json<Connection[]>();

    // Belt and braces with updateDataOnError: `connections` only ever holds a
    // real array, so consumers keep showing stale results instead of a broken
    // list, and `null` still means "not loaded yet".
    const connections = ref<Connection[] | null>(null);
    watch(
        data,
        (value) => {
            if (Array.isArray(value)) {
                connections.value = value;
            }
        },
        { immediate: true },
    );

    const fetchConnections = async () => {
        await execute();
    };

    return { connections, isFetchingConnections, searchQuery, connectionsFetchError, fetchConnections };
};
